/**
 * Metrics Provider Configuration
 * Environment-based configuration for analytics providers
 * Uses ad-blocker-resistant naming (metrics instead of analytics)
 */

import Analytics from 'analytics';
// @ts-expect-error - No type definitions available for @analytics/google-analytics
import googleAnalytics from '@analytics/google-analytics';
import { createFacebookPixelPlugin } from './providers/facebook';
import { createTikTokPixelPlugin } from './providers/tiktok';
import { createFirebaseAnalyticsPlugin } from './providers/firebase';
import { createAmplitudePlugin } from './providers/amplitude';
import { createDebugLogger } from '../../utils/debugLogger';

const logger = createDebugLogger('metrics');

/**
 * Get environment-specific metrics configuration
 */
const getMetricsConfig = () => {

    // In development, we can disable metrics or use test IDs
    const enableMetrics = process.env.REACT_APP_ENABLE_METRICS !== 'false';

    const plugins: any[] = [];

    // Firebase Analytics configuration (native Firebase Analytics)
    // This is separate from Google Analytics and provides Firebase-specific features
    if (enableMetrics && typeof window !== 'undefined' && process.env.REACT_APP_FIREBASE_USE_EMULATOR !== 'true') {
        plugins.push(createFirebaseAnalyticsPlugin());
    }

    // Google Analytics configuration (via gtag)
    // Use provided GA label or fallback to Firebase measurement ID
    const gaMeasurementId = process.env.REACT_APP_GA_MEASUREMENT_ID || 
                           process.env.REACT_APP_FIREBASE_MEASUREMENT_ID || 
                           'G-JY50537Y0V';
    if (gaMeasurementId && enableMetrics) {
        plugins.push(
            googleAnalytics({
                measurementIds: [gaMeasurementId],
            })
        );
    }

    // Facebook Pixel configuration
    const facebookPixelId = '2046716589403164';
    if (facebookPixelId && enableMetrics) {
        plugins.push(createFacebookPixelPlugin({ pixelId: facebookPixelId }));
    }

    // TikTok Pixel configuration
    const tiktokPixelId = 'D3OJPFRC77UD1HDJ8MCG';
    if (tiktokPixelId && enableMetrics) {
        plugins.push(createTikTokPixelPlugin({ pixelId: tiktokPixelId }));
    }

    // Amplitude Analytics configuration
    const amplitudeApiKey = '671d20e856fd9ea4eb79b9d72eeecd2d';
    if (amplitudeApiKey && enableMetrics) {
        plugins.push(createAmplitudePlugin({ apiKey: amplitudeApiKey }));
    }

    // Google Ads conversion tracking
    // Format: AW-683352602/IkZRCPyUhIYbEJrE7MUC
    // Conversion ID: AW-683352602
    // Conversion Label: IkZRCPyUhIYbEJrE7MUC
    const googleAdsConversionId = process.env.REACT_APP_GOOGLE_ADS_CONVERSION_ID || 'AW-683352602';
    const googleAdsConversionLabel = process.env.REACT_APP_GOOGLE_ADS_CONVERSION_LABEL || 'IkZRCPyUhIYbEJrE7MUC';
    if (googleAdsConversionId && googleAdsConversionLabel && enableMetrics) {
        // Capture values for use in closure
        const conversionId = googleAdsConversionId;
        const conversionLabel = googleAdsConversionLabel;
        
        plugins.push({
            name: 'google-ads',
            config: {
                conversionId,
                conversionLabel,
            },
            initialize: () => {
                // Google Ads conversion tracking is typically handled via gtag
                // which is already loaded with Google Analytics
                // No additional initialization needed
            },
            track: ({ payload }: any) => {
                const googleAdsLogger = createDebugLogger('googleAds');
                googleAdsLogger.track(payload.event, payload.properties);
                if (typeof window !== 'undefined' && (window as any).gtag) {
                    // Track conversion event for conversion completed only
                    if (payload.event === 'conversion_completed') {
                        // Generate unique transaction ID if not provided
                        // Prioritize transaction_id from payload, then price_id, then generate random
                        const transactionId = payload.properties?.transaction_id || 
                                            payload.properties?.price_id || 
                                            `txn_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
                        
                        // Build conversion event with items array for ecommerce tracking
                        const conversionData: any = {
                            'send_to': `${conversionId}/${conversionLabel}`,
                            'value': payload.properties?.value || 0,
                            'currency': payload.properties?.currency || 'USD',
                            'transaction_id': transactionId,
                        };
                        
                        // Add items array if available (Google Ads supports ecommerce items)
                        if (payload.properties?.items && Array.isArray(payload.properties.items) && payload.properties.items.length > 0) {
                            conversionData.items = payload.properties.items.map((item: any) => ({
                                item_id: item.item_id || item.id || '',
                                item_name: item.item_name || item.name || '',
                                item_category: item.item_category || item.category || 'membership',
                                price: item.price !== undefined ? item.price : 0,
                                quantity: item.quantity !== undefined ? item.quantity : 1,
                            }));
                        }
                        
                        // Add other properties
                        Object.keys(payload.properties || {}).forEach((key) => {
                            if (!['transaction_id', 'value', 'currency', 'items'].includes(key)) {
                                conversionData[key] = payload.properties[key];
                            }
                        });
                        
                        (window as any).gtag('event', 'conversion', conversionData);
                    }
                }
            },
        });
    }

    logger.info(`Initialized ${plugins.length} metrics plugins:`, plugins.map(p => p.name).join(', '));

    return {
        app: 'fanclub-signup',
        plugins,
        enabled: enableMetrics, // Enable metrics in all environments if enableMetrics is true
    };
};

/**
 * Initialize and return metrics instance
 * Returns null if metrics should be disabled
 */
export const initializeMetrics = () => {
    try {
        const config = getMetricsConfig();
        
        if (!config.enabled || config.plugins.length === 0) {
            // Return a no-op instance in development or when disabled
            return {
                recordEvent: () => {},
                identify: () => {},
                page: () => {},
            };
        }

        const instance = Analytics(config);
        
        return {
            recordEvent: instance.track.bind(instance),
            identify: instance.identify.bind(instance),
            page: instance.page.bind(instance),
        };
    } catch (error) {
        // Gracefully handle initialization failures
        console.warn('Metrics initialization failed:', error);
        return {
            recordEvent: () => {},
            identify: () => {},
            page: () => {},
        };
    }
};

